import { MatTableModule } from '@angular/material/table';
import { Component, DestroyRef, OnInit, computed, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { OuiButtonModule } from 'omnium-ui/button';
import { OuiSelect, OuiSelectOption } from 'omnium-ui/form-field';
import { OuiInfosBulle } from 'omnium-ui/infos-bulle';
import { OuiSnackbarService } from 'omnium-ui/snackbar';
import { OuiTableModule } from 'omnium-ui/table';
import { OuiTagModule } from 'omnium-ui/tag';
import { appRoutePaths } from '../../../../app.paths';
import { ActionAuthorizationService, doublonsBusterActionCodes } from '../../../../core/auth/action-authorization.service';
import {
  CorrectionExecution,
  CorrectionPreview,
  MetadataDiagnostic,
  MetadataCorrectionWorkspace,
  MinotaureRecueilCrossConsultantRecueilObject,
  ProblematicMetadataRow,
  SuggestedInvestorDeletion,
} from '../../../../core/models/api.models';
import { DoublonsBusterApiService } from '../../../../core/services/doublons-buster-api.service';
import { buildReplacementOptionChoices, buildReplacementSelections } from '../../shared/correction-preview.utils';
import {
  buildCorrectionExecutionRequest,
  canExecuteCorrection,
  getPreviewableCandidateInvestorId,
  isCorrectionBlocked,
  shouldSkipCorrectionPreview,
} from '../../shared/correction-session.utils';
import { InvestorPersonMappingConflictsTableComponent } from '../../shared/investor-person-mapping-conflicts-table/investor-person-mapping-conflicts-table.component';
import { InvestorDeletionScoreDetailsComponent } from '../../shared/investor-deletion-score-details/investor-deletion-score-details.component';
import { InvestorStatusTagComponent } from '../../shared/investor-status-tag/investor-status-tag.component';
import { StateCardComponent } from '../../shared/state-card/state-card.component';
import { SummaryGridComponent, SummaryGridItem } from '../../shared/summary-grid/summary-grid.component';
import { InvestorCardComponent } from '../../../../core/components/investor-card/investor-card.component';
import { ConsultantCardComponent } from '../../../../core/components/consultant-card/consultant-card.component';

type MetadataWorkspaceDiagnostic = {
  key: string;
  label: string;
  status: 'completed' | 'error' | 'pending' | 'to-correct';
};

@Component({
  selector: 'dbw-metadata-workspace',
  imports: [
    MatTableModule,
    OuiButtonModule,
    OuiSelect,
    OuiInfosBulle,
    OuiTableModule,
    OuiTagModule,
    InvestorDeletionScoreDetailsComponent,
    InvestorPersonMappingConflictsTableComponent,
    InvestorStatusTagComponent,
    StateCardComponent,
    SummaryGridComponent,
    InvestorCardComponent,
    ConsultantCardComponent,
  ],
  templateUrl: './metadata-workspace.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./metadata-workspace.component.scss'],
})
export class MetadataWorkspaceComponent implements OnInit {
  readonly rowColumns = ['investor', 'consultant', 'status', 'arianeId'];
  readonly anomalyColumns = ['detection', 'investor', 'consultant', 'status'];
  readonly recueilColumns = ['recueil', 'consultants', 'investors'];
  readonly candidateColumns = ['rank', 'score', 'investor', 'status', 'gedDocuments', 'boxOperations', 'immoOptionIntention', 'actions'];
  readonly destroyRef = inject(DestroyRef);

  readonly metadataId = signal(0);
  readonly loading = signal(true);
  readonly previewLoading = signal(false);
  readonly executing = signal(false);
  readonly error = signal<string | undefined>(undefined);
  readonly recueilChecksLoading = signal(false);
  readonly recueilChecksError = signal<string | undefined>(undefined);
  readonly previewError = signal<string | undefined>(undefined);
  readonly workspace = signal<MetadataCorrectionWorkspace | undefined>(undefined);
  readonly recueilCrossConsultantRows = signal<MinotaureRecueilCrossConsultantRecueilObject[]>([]);
  readonly preview = signal<CorrectionPreview | undefined>(undefined);
  readonly execution = signal<CorrectionExecution | undefined>(undefined);
  readonly selectedInvestorId = signal<number | undefined>(undefined);
  readonly previewInvestorId = signal<number | undefined>(undefined);
  readonly replacementSelections = signal<Record<number, number | null>>({});
  readonly replacementOptionChoices = signal<Partial<Record<number, OuiSelectOption<number>[]>>>({});
  readonly virtualDeletedInvestorIds = signal<number[]>([]);

  readonly firstGroupCandidates = computed<SuggestedInvestorDeletion[]>(
    () => this.workspace()?.duplicateAnalysis.firstDuplicateGroup?.rankedCandidates ?? []
  );

  readonly correctionBlocked = computed(() => isCorrectionBlocked(this.workspace()));
  readonly canExecuteMetadataCorrectionAction = computed(() =>
    this.actionAuthorizationService.canExecute(doublonsBusterActionCodes.problematicMetadataCorrectionExecute)
  );

  readonly canExecute = computed(() => {
    return (
      this.canExecuteMetadataCorrectionAction() &&
      canExecuteCorrection(this.preview(), this.executing(), this.correctionBlocked(), this.replacementSelections())
    );
  });

  readonly pageTitle = computed(() => {
    const workspace = this.workspace();
    const names = this.getRecueilInvestorNames(workspace);

    if (names.length > 0) {
      return `Recueil ${names.map(name => `${name}`).join(' / ')}`;
    }

    return `Recueil metadata #${this.metadataId()}`;
  });

  readonly summaryItems = computed<SummaryGridItem[]>(() => {
    const workspace = this.workspace();

    if (!workspace) {
      return [];
    }

    return [
      {
        key: 'metadata-id',
        label: 'Metadata',
        value: workspace.metadataId,
        meta: 'Identifiant de la metadata.',
      },
      {
        key: 'duplicates',
        label: 'Doublons',
        value: workspace.duplicateAnalysis.duplicateGroups.length,
        aside: workspace.hasDuplicates ? { label: 'À corriger', status: 'to-correct' } : { label: 'OK', status: 'completed' },
      },
      {
        key: 'recueil-cross-consultant',
        label: 'Recueil inter-consultants',
        value: this.recueilChecksLoading() ? '...' : this.recueilChecksError() ? 'Erreur' : this.recueilCrossConsultantRows().length,
        meta: 'Elements de recueil partagés',
        aside: this.recueilChecksLoading()
          ? { label: 'Chargement', status: 'pending' }
          : this.recueilChecksError()
            ? { label: 'Erreur', status: 'error' }
            : this.recueilCrossConsultantRows().length > 0
              ? { label: 'À contrôler', status: 'error' }
              : { label: 'OK', status: 'completed' },
      },
    ];
  });

  readonly diagnostics = computed<MetadataWorkspaceDiagnostic[]>(() => {
    const workspace = this.workspace();

    if (!workspace) {
      return [];
    }

    const diagnostics: MetadataWorkspaceDiagnostic[] = workspace.diagnostics.map(diagnostic => ({
      key: diagnostic.kind,
      label: `${diagnostic.label} (${diagnostic.rows.length} ligne(s))`,
      status: this.getDiagnosticStatus(diagnostic),
    }));

    if (diagnostics.length === 0) {
      diagnostics.push({
        key: 'metadata-classifier',
        label: 'Aucun diagnostic metadata',
        status: 'completed',
      });
    }

    return [
      ...diagnostics,
      {
        key: 'recueil-cross-consultant',
        label: this.getRecueilCheckLabel(),
        status: this.getRecueilCheckStatus(),
      },
    ];
  });

  readonly diagnosticSummaryMessage = computed(() => {
    const workspace = this.workspace();

    if (!workspace) {
      return '';
    }

    const parts: string[] = [];

    workspace.diagnostics.forEach(diagnostic => {
      parts.push(`${diagnostic.label}: ${diagnostic.rows.length} ligne(s)`);
    });

    if (this.recueilChecksLoading()) {
      parts.push('chargement du diagnostic recueil inter-consultants');
    } else if (this.recueilChecksError()) {
      parts.push('diagnostic recueil inter-consultants indisponible');
    } else if (this.recueilCrossConsultantRows().length > 0) {
      parts.push(`${this.recueilCrossConsultantRows().length} élément(s) recueil partagé(s) entre consultants`);
    }

    return parts.length === 0 ? workspace.summaryMessage : parts.join(' • ');
  });

  readonly anomalyDiagnostics = computed(() =>
    (this.workspace()?.diagnostics ?? []).filter(diagnostic => diagnostic.severity === 'anomaly')
  );

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly apiService: DoublonsBusterApiService,
    private readonly actionAuthorizationService: ActionAuthorizationService,
    private readonly snackbarService: OuiSnackbarService
  ) {}

  ngOnInit(): void {
    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(paramMap => {
      const metadataId = Number(paramMap.get('metadataId'));

      if (!Number.isFinite(metadataId) || metadataId <= 0) {
        this.error.set('Metadata invalide.');
        this.loading.set(false);
        return;
      }

      this.metadataId.set(metadataId);
      this.virtualDeletedInvestorIds.set([]);
      this.clearPreviewState();
      this.execution.set(undefined);
      this.loadWorkspace();
    });
  }

  navigateBack(): void {
    this.router.navigateByUrl(appRoutePaths.minotaure.reporting);
  }

  navigateToBatch(): void {
    this.router.navigateByUrl(appRoutePaths.minotaure.batch);
  }

  reload(): void {
    this.loadWorkspace();
  }

  resetSimulation(): void {
    this.virtualDeletedInvestorIds.set([]);
    this.clearPreviewState();
    this.execution.set(undefined);
    this.loadWorkspace();
  }

  selectCandidate(candidate: SuggestedInvestorDeletion): void {
    this.previewDeletion(getPreviewableCandidateInvestorId(candidate));
  }

  previewDeletion(investorId: number): void {
    if (!this.canExecuteMetadataCorrectionAction() || this.correctionBlocked()) {
      return;
    }

    if (shouldSkipCorrectionPreview(investorId, this.previewLoading(), this.previewInvestorId(), this.preview())) {
      return;
    }

    this.selectedInvestorId.set(investorId);
    this.previewInvestorId.set(investorId);
    this.preview.set(undefined);
    this.execution.set(undefined);
    this.previewError.set(undefined);
    this.previewLoading.set(true);
    this.replacementSelections.set({});
    this.replacementOptionChoices.set({});

    this.apiService.previewCorrection(this.metadataId(), investorId, this.virtualDeletedInvestorIds()).subscribe({
      next: preview => {
        this.preview.set(preview);
        this.replacementSelections.set(buildReplacementSelections(preview));
        this.replacementOptionChoices.set(buildReplacementOptionChoices(preview));
        this.previewLoading.set(false);
      },
      error: error => {
        this.previewError.set(error?.error?.detail ?? "Impossible d'analyser la suppression demandée.");
        this.previewLoading.set(false);
      },
    });
  }

  onReplacementSelected(linkedInvestorId: number, replacementInvestorId: number | null): void {
    this.replacementSelections.update(selections => ({
      ...selections,
      [linkedInvestorId]: replacementInvestorId,
    }));
  }

  executeCorrection(): void {
    const selectedInvestorId = this.selectedInvestorId();
    const preview = this.preview();

    if (!selectedInvestorId || !preview || !this.canExecute()) {
      return;
    }

    this.executing.set(true);
    this.previewError.set(undefined);

    const request = buildCorrectionExecutionRequest(
      preview,
      selectedInvestorId,
      this.virtualDeletedInvestorIds(),
      this.replacementSelections()
    );

    this.apiService.executeCorrection(this.metadataId(), request).subscribe({
      next: execution => {
        this.execution.set(execution);
        this.virtualDeletedInvestorIds.set(execution.updatedVirtualDeletedInvestorIds);
        this.executing.set(false);

        if (execution.success) {
          this.snackbarService.open('Correction exécutée avec succès.', 'success');
          this.clearPreviewState();
          this.loadWorkspace();
        } else {
          this.snackbarService.open('La correction a échoué.', 'error');
        }
      },
      error: error => {
        this.executing.set(false);
        const message = error?.error?.detail ?? "L'exécution de la correction a échoué.";
        this.previewError.set(message);
        this.snackbarService.open(message, 'error');
      },
    });
  }

  getStepStatus(step: { success: boolean; executed: boolean }): 'completed' | 'error' | 'pending' {
    if (!step.executed) {
      return 'pending';
    }

    return step.success ? 'completed' : 'error';
  }

  formatGedDocumentCount(gedDocumentCount: number | null | undefined): string {
    if (gedDocumentCount === null || gedDocumentCount === undefined) {
      return 'Indisponible';
    }

    return `${gedDocumentCount}`;
  }

  hasGedDocumentNames(investor: ProblematicMetadataRow): boolean {
    return investor.gedDocumentNames.length > 0;
  }

  getGedDocumentNamesTitle(investor: ProblematicMetadataRow): string {
    return `Documents GED investisseur ${investor.investorId}`;
  }

  getBoxPlacementOperationsLabel(hasBoxPlacementOperations: boolean | null | undefined): string {
    if (hasBoxPlacementOperations === null || hasBoxPlacementOperations === undefined) {
      return 'Indisponible';
    }

    return hasBoxPlacementOperations ? 'Oui' : 'Non';
  }

  getBoxPlacementOperationsStatus(hasBoxPlacementOperations: boolean | null | undefined): 'completed' | 'todo' | 'pending' {
    if (hasBoxPlacementOperations === null || hasBoxPlacementOperations === undefined) {
      return 'pending';
    }

    return hasBoxPlacementOperations ? 'todo' : 'completed';
  }

  getImmoOptionIntentionLabel(hasImmoOptionIntention: boolean | null | undefined): string {
    if (hasImmoOptionIntention === null || hasImmoOptionIntention === undefined) {
      return 'Indisponible';
    }

    return hasImmoOptionIntention ? 'Oui' : 'Non';
  }

  getImmoOptionIntentionStatus(hasImmoOptionIntention: boolean | null | undefined): 'completed' | 'todo' | 'pending' {
    if (hasImmoOptionIntention === null || hasImmoOptionIntention === undefined) {
      return 'pending';
    }

    return hasImmoOptionIntention ? 'todo' : 'completed';
  }

  getRecueilCheckStatus(): 'completed' | 'error' | 'pending' {
    if (this.recueilChecksLoading()) {
      return 'pending';
    }

    return this.recueilChecksError() || this.recueilCrossConsultantRows().length > 0 ? 'error' : 'completed';
  }

  getRecueilCheckLabel(): string {
    if (this.recueilChecksLoading()) {
      return 'Contrôle en cours';
    }

    if (this.recueilChecksError()) {
      return 'Contrôle indisponible';
    }

    const count = this.recueilCrossConsultantRows().length;

    return count === 0 ? 'Aucun problème recueil' : `${count} élément(s) à contrôler`;
  }

  getDiagnosticStatus(diagnostic: MetadataDiagnostic): 'error' | 'to-correct' {
    return diagnostic.severity === 'correction' ? 'to-correct' : 'error';
  }

  private loadWorkspace(): void {
    this.loading.set(true);
    this.error.set(undefined);
    this.recueilChecksLoading.set(false);
    this.recueilChecksError.set(undefined);
    this.recueilCrossConsultantRows.set([]);

    this.apiService.getMetadataWorkspace(this.metadataId(), this.virtualDeletedInvestorIds()).subscribe({
      next: workspace => {
        this.workspace.set(workspace);
        this.loading.set(false);
        this.loadRecueilCrossConsultantChecks(workspace);

        const selectedInvestorId = this.selectedInvestorId();

        if (
          selectedInvestorId &&
          (workspace.hasInvestorPersonMappingConflicts || !workspace.duplicateAnalysis.allowedInvestorIds.includes(selectedInvestorId))
        ) {
          this.clearPreviewState();
        }
      },
      error: error => {
        this.error.set(error?.error?.detail ?? 'Impossible de charger la metadata.');
        this.recueilChecksLoading.set(false);
        this.loading.set(false);
      },
    });
  }

  private loadRecueilCrossConsultantChecks(workspace: MetadataCorrectionWorkspace): void {
    if (workspace.metadataId <= 0) {
      this.recueilCrossConsultantRows.set([]);
      this.recueilChecksLoading.set(false);
      this.recueilChecksError.set(undefined);
      return;
    }

    this.recueilChecksLoading.set(true);
    this.recueilChecksError.set(undefined);
    this.recueilCrossConsultantRows.set([]);

    this.apiService.getMinotaureRecueilCrossConsultantPage(0, 1, { metadataId: workspace.metadataId }).subscribe({
      next: page => {
        this.recueilCrossConsultantRows.set(page.items[0]?.recueilObjects ?? []);
        this.recueilChecksLoading.set(false);
      },
      error: error => {
        this.recueilChecksError.set(error?.error?.detail ?? 'Impossible de charger les contrôles recueil inter-consultants.');
        this.recueilCrossConsultantRows.set([]);
        this.recueilChecksLoading.set(false);
      },
    });
  }

  private clearPreviewState(): void {
    this.selectedInvestorId.set(undefined);
    this.previewInvestorId.set(undefined);
    this.preview.set(undefined);
    this.previewError.set(undefined);
    this.previewLoading.set(false);
    this.replacementSelections.set({});
    this.replacementOptionChoices.set({});
  }

  private getRecueilInvestorNames(workspace: MetadataCorrectionWorkspace | undefined): string[] {
    const investors = workspace?.duplicateAnalysis.firstDuplicateGroup?.investors ?? workspace?.rows ?? [];
    const names = investors.map(investor => investor.displayName?.trim()).filter((name): name is string => !!name);

    return [...new Set(names)].slice(0, 2);
  }
}
